import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function AddReferenceButton({
  onOpenSelector,
  onSelectCanvas,
  testId = 'add-reference-btn',
}: {
  onOpenSelector: () => void
  onSelectCanvas?: () => void
  testId?: string
}) {
  const { t } = useTranslation('canvas')
  const button = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{
    left: number
    bottom: number
  } | null>(null)
  useEffect(() => {
    if (!position) return
    menu.current?.querySelector('button')?.focus()
    const close = (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        !menu.current?.contains(event.target) &&
        !button.current?.contains(event.target)
      )
        setPosition(null)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [position])
  const choose = (action: () => void) => {
    setPosition(null)
    action()
  }
  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={() => {
          if (!onSelectCanvas) {
            onOpenSelector()
            return
          }
          const rect = button.current?.getBoundingClientRect()
          if (rect)
            setPosition(
              position
                ? null
                : {
                    left: Math.max(
                      8,
                      Math.min(rect.left, window.innerWidth - 208),
                    ),
                    bottom: window.innerHeight - rect.top + 6,
                  },
            )
        }}
        aria-haspopup={onSelectCanvas ? 'menu' : undefined}
        aria-expanded={onSelectCanvas ? !!position : undefined}
        className="nodrag shrink-0 flex items-center justify-center rounded-lg border border-dashed border-input-border bg-surface-1 text-text-muted hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary"
        style={{ width: 45, height: 45 }}
        title={t('configTabContent.addReference')}
        data-testid={testId}
      >
        <Plus className="w-4 h-4" />
      </button>
      {position &&
        createPortal(
          <div
            ref={menu}
            role="menu"
            data-testid="add-reference-menu"
            className="fixed z-[9999] w-48 rounded-lg border border-border bg-background p-1 shadow-xl"
            style={position}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation()
                setPosition(null)
                button.current?.focus()
              }
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                const items = Array.from(
                  menu.current?.querySelectorAll('button') ?? [],
                )
                const index = items.findIndex(
                  (item) => item === document.activeElement,
                )
                items[
                  (index +
                    (event.key === 'ArrowDown' ? 1 : -1) +
                    items.length) %
                    items.length
                ]?.focus()
              }
            }}
          >
            <button
              role="menuitem"
              className="w-full rounded-md px-3 py-2 text-left text-base text-foreground hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
              onClick={() => choose(() => onSelectCanvas?.())}
            >
              {t('referenceInteraction.selectCanvas')}
            </button>
            <button
              role="menuitem"
              className="w-full rounded-md px-3 py-2 text-left text-base text-foreground hover:bg-surface-2 focus:bg-surface-2 focus:outline-none"
              onClick={() => choose(onOpenSelector)}
            >
              {t('referenceInteraction.selector')}
            </button>
          </div>,
          document.body,
        )}
    </>
  )
}
